import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TabComponent, TabModule } from '@syncfusion/ej2-angular-navigations';
import { AssetStorageEntryLicense, ImageQuality, UpdateAssetStorageEntryDTO } from 'src/app/base/models/models.swagger.generated';
import { ErrorType } from 'src/app/core/routing/error-types';
import { ToastService } from 'src/app/base/services/toast.service';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { AssetStorageUploadEditorFormType } from '../shared/asset-storage-edit-detail-form/asset-storage-upload-editor-form-type';
import { IAssetStorageEntry } from '../../asset-storage-entry';
import { AssetStorageService } from '../../asset-storage.service';
import { TranslateModule } from '@ngx-translate/core';

import { FileInfo } from '@syncfusion/ej2-angular-inputs';
import { AssetStorageEditTabGroupComponent } from "../shared/asset-storage-edit-tab-group/asset-storage-edit-tab-group.component";

@Component({
    selector: 'app-asset-storage-single-edit',
    templateUrl: './asset-storage-single-edit.component.html',
    styleUrls: ['./asset-storage-single-edit.component.scss'],
    imports: [DialogModule, TabModule, TranslateModule, AssetStorageEditTabGroupComponent]
})
export class AssetStorageSingleEditComponent implements OnInit {

  @ViewChild('tabObj')
  public tabObj: TabComponent;

  @ViewChild("editProductAssetDialog")
  public dialog: DialogComponent;

  @ViewChild("assetStorageEditTabGroupComponent")
  public assetStorageEditTabGroupComponent: AssetStorageEditTabGroupComponent;

  @Output()
  public OnClose: EventEmitter<any> = new EventEmitter<any>();

  public get canSave() {
    return this.assetStorageUploadEditorForm.valid && !this.assetStorageEditTabGroupComponent?.assetImageEditorComponent?.hasPendingChanges();
  }

  public assetStorageUploadEditorForm: FormGroup<AssetStorageUploadEditorFormType>;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private assetStorageService: AssetStorageService,
  ) { }

  public ngOnInit(): void {
    this.assetStorageUploadEditorForm = this.fb.group<AssetStorageUploadEditorFormType>({
      id: this.fb.control<number>(null, [Validators.required]),
      language: this.fb.control<string>(null, [Validators.required]),
      source: this.fb.control<string>(null),
      license: this.fb.control<AssetStorageEntryLicense>(null, [Validators.required]),
      imageQuality: this.fb.control<ImageQuality>(null),
      path: this.fb.control<string>(null),
      fileExtension: this.fb.control<string>(null),
      base64FileData: this.fb.control<string>(null),
      originalFileName: this.fb.control<string>({ value: null, disabled: true }),
      assignedAssetKeyWords: this.fb.control<number[]>(null),
      totalSize: this.fb.control<number>(null),
      isStaticFileName: this.fb.control<boolean>(false),
      fileLink: this.fb.control<string>(null),
      intermediateFileInfo: this.fb.control<FileInfo>(null)
    });
  }

  public editAsset(asset: IAssetStorageEntry, uploadPath: string) {
    this.fillForm(asset, uploadPath);
    this.dialog.show(false);
  }

  public onCancel() {
    this.dialog.hide();
    this.resetDialog();
  }

  public onOpen(args: any): void {
    //Preventing the default dialog focus
    args.preventFocus = true;
  }

  public onSave() {
    if (!this.assetStorageEditTabGroupComponent.canSave()) {
      return;
    }

    if (!this.assetStorageUploadEditorForm.valid) {
      this.assetStorageUploadEditorForm.markAllAsTouched();
      this.assetStorageUploadEditorForm.markAsDirty();
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.assets.asset-storage.edit-asset-storage.form-invalid.title',
          'i18n.domain.assets.asset-storage.edit-asset-storage.form-invalid.message', null);
      }
      return;
    }

    var extension = this.assetStorageUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    if ((extension == "jpg" ||
      extension == "jpeg" ||
      extension == "png")) {
      if (this.assetStorageEditTabGroupComponent.assetImageEditorComponent?.imageEditorObj && this.assetStorageEditTabGroupComponent.assetImageEditorComponent?.hasBeenEdited()) {
        this.assetStorageEditTabGroupComponent.getImageDataFromCanvas();
      }
    }
    this.assetStorageService.updateAssetStorageEntry(this.fillUpdateAssetsStorageEntryDTO()).subscribe({
      next: () => {
        this.OnClose.emit();
        this.dialog.hide();
        this.resetDialog();
      }
    });
  }

  public setScreenSize(isFullScreen: boolean) {
    isFullScreen ? this.dialog.show(true) : this.dialog.show(false);
  }

  private resetDialog() {
    this.assetStorageUploadEditorForm.setValue({
      id: null,
      language: null,
      source: null,
      license: null,
      path: null,
      fileExtension: null,
      base64FileData: null,
      imageQuality: null,
      originalFileName: null,
      assignedAssetKeyWords: [],
      totalSize: null,
      isStaticFileName: false,
      fileLink: null,
      intermediateFileInfo: null
    });
    this.assetStorageUploadEditorForm.markAsUntouched();
    this.assetStorageUploadEditorForm.markAsPristine();
    this.assetStorageUploadEditorForm.updateValueAndValidity();
    this.tabObj?.select(0);
    this.assetStorageEditTabGroupComponent?.assetEditDetailComponent?.resetMultiselectFilter();
  }

  private fillUpdateAssetsStorageEntryDTO(): UpdateAssetStorageEntryDTO {
    return new UpdateAssetStorageEntryDTO({
      id: this.assetStorageUploadEditorForm.controls.id.value,
      languageCodeIso3: this.assetStorageUploadEditorForm.controls.language.value,
      license: this.assetStorageUploadEditorForm.controls.license.value,
      source: this.assetStorageUploadEditorForm.controls.source.value,
      base64FileData: this.assetStorageUploadEditorForm.controls.base64FileData.value,
      imageQuality: this.assetStorageUploadEditorForm.controls.imageQuality.value,
      assignedAssetKeyWords: this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.value,
    });
  }

  private fillForm(assetStorageEntry: IAssetStorageEntry, uploadPath: string) {
    const keywordIds = assetStorageEntry?.assignedKeyWords?.map((keyword) => keyword.id);
    this.assetStorageUploadEditorForm.setValue({
      id: assetStorageEntry?.entryId ?? null,
      language: assetStorageEntry?.languageCodeIso3 ?? null,
      license: assetStorageEntry?.license ?? null,
      source: assetStorageEntry?.source ?? null,
      path: uploadPath,
      fileExtension: assetStorageEntry?.fileLink.split('.').pop() ?? null,
      base64FileData: null,
      imageQuality: assetStorageEntry?.imageQuality ?? null,
      originalFileName: assetStorageEntry?.name ?? null,
      assignedAssetKeyWords: keywordIds ?? [],
      totalSize: null,
      isStaticFileName: assetStorageEntry?.isStaticFileName ?? false,
      fileLink: assetStorageEntry?.fileLink ?? null,
      intermediateFileInfo: null
    });

    if (assetStorageEntry?.isStaticFileName && assetStorageEntry?.entryId) {
      this.assetStorageUploadEditorForm.controls.originalFileName.enable();
    } else {
      this.assetStorageUploadEditorForm.controls.originalFileName.disable();
    }
  }
}
